import { createSlice } from '@reduxjs/toolkit';
import axios, { AxiosError } from 'axios';
import createAppAsyncThunk from '../../store/createAppAsyncThunk';
import { AsyncStateType, RootStateType } from '../../store/types';
import RepType from './RepType';

type AppRootStateType = RootStateType<repSliceType>;

/**
 * Get rep detail from server
 */
export const getRepFolderInfo = createAppAsyncThunk<RepType, { folderName: string; cid: string }>(
	'rep/getRepFolderInfo',
	async ({ folderName, cid }, { rejectWithValue }) => {
		try {
			const response = await axios.post<RepType[]>('rep/getRepFolderInfo', { folderName, cid });

			if (response.status !== 200) {
				return rejectWithValue((response.data as any)?.message || 'Failed to load rep data');
			}

			const data = response.data?.[0] as RepType | undefined;
			if (!data) {
				return rejectWithValue('Rep folder info not found');
			}

			return data;
		} catch (error) {
			const axiosError = error as AxiosError;
			return rejectWithValue(axiosError.message || 'An unexpected error occurred');
		}
	}
);

const initialState: AsyncStateType<RepType> = {
	data:
	{
		id: 0,
		repId: '',
		repName: 'Corporate Office',
		repEmail: 'info@myuwe.net',
		repPhone: '2488487065',
		repTitle: '',
		repFolder: '',
		isAgentCertified: 0,
		randrLevel: 0,
		medClubLevel: 0,
		isPPActive: 'Y'
	},
	status: 'idle'
};

/**
 * The Reps App Rep slice.
 */
export const repSlice = createSlice({
	name: 'rep',
	initialState,
	reducers: {
		resetRep: () => initialState
	},
	extraReducers: (builder) => {
		builder
			.addCase(getRepFolderInfo.pending, (state) => {
				state.status = 'loading';
			})
			.addCase(getRepFolderInfo.rejected, (state) => {
				state.status = 'failed';
			})
			.addCase(getRepFolderInfo.fulfilled, (state, action) => {
				state.data = action.payload;
				state.status = 'succeeded';
			});
	}
});

export const selectRep = (state: AppRootStateType) => state.rep;

export const { resetRep } = repSlice.actions;

export type repSliceType = typeof repSlice;

export default repSlice.reducer;
